import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import type { Account, AgencyCopyResult, AgencyCopyItemStatus } from '../../api/client';
import { AccountAvatar } from '../AccountAvatar';
import { FeatherIcon } from '../FeatherIcon';
import { Spinner, LoadingBlock } from '../Spinner';
import { useSidebar } from '../../context/SidebarContext';

/**
 * Copy-item registry. Keys MUST match backend/routes/agency.py VALID_ITEMS.
 * `note` marks items with cross-account caveats the user should see up front.
 */
interface CopyItemDef {
  key: string;
  note?: boolean;
}

interface CopyGroupDef {
  key: string;
  icon: string;
  items: CopyItemDef[];
}

const COPY_GROUPS: CopyGroupDef[] = [
  {
    key: 'aiSetup',
    icon: 'sliders',
    items: [
      { key: 'setup_working_scope', note: true },
      { key: 'setup_base_regime' },
      { key: 'setup_ignore_lists', note: true },
      { key: 'setup_work_schedule' },
      { key: 'setup_hot_transition' },
      { key: 'setup_follow_ups' },
      { key: 'setup_folder_overrides', note: true },
      { key: 'new_fan_flow' },
    ],
  },
  {
    key: 'modelInfo',
    icon: 'user',
    items: [
      { key: 'style_normal_chat' },
      { key: 'style_hot_chat' },
      { key: 'model_bio' },
    ],
  },
  {
    key: 'notifications',
    icon: 'bell',
    items: [
      { key: 'telegram_chat_id', note: true },
      { key: 'interrupt_list_name', note: true },
      { key: 'notification_settings' },
      { key: 'fan_info' },
    ],
  },
  {
    key: 'censure',
    icon: 'shield',
    items: [
      { key: 'censure_banned_words' },
      { key: 'censure_banned_topics' },
      { key: 'censure_not_preferable' },
    ],
  },
  {
    key: 'monetisation',
    icon: 'dollar-sign',
    items: [
      { key: 'monetisation_prices', note: true },
      { key: 'sales_ladder', note: true },
      { key: 'monetisation_hot_tips' },
      { key: 'monetisation_normal_tips' },
      { key: 'monetisation_vip_offer' },
    ],
  },
  {
    key: 'massMessages',
    icon: 'send',
    items: [{ key: 'mass_lists', note: true }, { key: 'mass_campaigns' }],
  },
  {
    key: 'priorityMessages',
    icon: 'zap',
    items: [{ key: 'priority_lists', note: true }, { key: 'priority_campaigns' }],
  },
  {
    key: 'automatedMessages',
    icon: 'clock',
    items: [
      { key: 'automated_messages', note: true },
      { key: 'online_campaign', note: true },
      { key: 'online_audience', note: true },
    ],
  },
];

const ALL_ITEM_KEYS = COPY_GROUPS.flatMap((g) => g.items.map((i) => i.key));

function accountLabel(a: Account): string {
  return a.profile_name || a.username || a.account_id;
}

const STATUS_META: Record<AgencyCopyItemStatus, { icon: string; className: string }> = {
  copied: { icon: 'check-circle', className: 'text-[var(--color-success,#3ddc84)]' },
  skipped: { icon: 'minus-circle', className: 'text-[var(--color-text-tertiary)]' },
  forbidden: { icon: 'lock', className: 'text-[var(--color-warning,#f5a623)]' },
  error: { icon: 'x-circle', className: 'text-[var(--color-danger,#ff5c5c)]' },
};

export function AgencyCopyTab() {
  const { t } = useTranslation('agency');
  const { sidebarWidth } = useSidebar();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [sourceId, setSourceId] = useState<string | null>(null);
  const [targetIds, setTargetIds] = useState<Set<string>>(new Set());
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [sourceSearch, setSourceSearch] = useState('');
  const [targetSearch, setTargetSearch] = useState('');

  const [copying, setCopying] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [result, setResult] = useState<AgencyCopyResult | null>(null);
  const [openResults, setOpenResults] = useState<Set<string>>(new Set());

  useEffect(() => {
    api
      .getAccounts()
      .then((data) => setAccounts(data))
      .catch((e) => {
        console.error('Failed to load accounts:', e);
        setLoadError(t('copy.loadAccountsFailed'));
      })
      .finally(() => setLoadingAccounts(false));
  }, [t]);

  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    accounts.forEach((a) => m.set(a.account_id, accountLabel(a)));
    return m;
  }, [accounts]);

  const filterAccounts = (query: string, exclude?: string | null) => {
    const q = query.trim().toLowerCase();
    return accounts.filter((a) => {
      if (exclude && a.account_id === exclude) return false;
      if (!q) return true;
      return (
        (a.profile_name || '').toLowerCase().includes(q) ||
        (a.username || '').toLowerCase().includes(q) ||
        a.account_id.toLowerCase().includes(q)
      );
    });
  };

  const sourceCandidates = filterAccounts(sourceSearch);
  const targetCandidates = filterAccounts(targetSearch, sourceId);
  const visibleTargetIds = targetCandidates.map((a) => a.account_id);

  const pickSource = (id: string) => {
    setSourceId(id);
    setResult(null);
    setTargetIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const toggleTarget = (id: string) => {
    setResult(null);
    setTargetIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleItem = (key: string) => {
    setResult(null);
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleGroup = (group: CopyGroupDef) => {
    setResult(null);
    setSelectedItems((prev) => {
      const next = new Set(prev);
      const allOn = group.items.every((i) => next.has(i.key));
      group.items.forEach((i) => (allOn ? next.delete(i.key) : next.add(i.key)));
      return next;
    });
  };

  const toggleAllItems = () => {
    setResult(null);
    setSelectedItems((prev) =>
      prev.size === ALL_ITEM_KEYS.length ? new Set() : new Set(ALL_ITEM_KEYS),
    );
  };

  const toggleAllVisibleTargets = () => {
    setResult(null);
    setTargetIds((prev) => {
      const allOn = visibleTargetIds.length > 0 && visibleTargetIds.every((id) => prev.has(id));
      const next = new Set(prev);
      visibleTargetIds.forEach((id) => (allOn ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  const canCopy = Boolean(sourceId) && targetIds.size > 0 && selectedItems.size > 0 && !copying;

  const handleCopy = async () => {
    if (!sourceId || !canCopy) return;
    setCopying(true);
    setCopyError(null);
    setResult(null);
    try {
      // Preserve registry order so results line up with the checklist.
      const items = ALL_ITEM_KEYS.filter((k) => selectedItems.has(k));
      const res = await api.copyAgencySettings(sourceId, Array.from(targetIds), items);
      setResult(res);
      setOpenResults(new Set(res.results.filter((r) => r.items.some((i) => i.status === 'error' || i.status === 'forbidden')).map((r) => r.account_id)));
    } catch (e) {
      console.error('Copy failed:', e);
      setCopyError(e instanceof Error ? e.message : t('copy.copyFailed'));
    } finally {
      setCopying(false);
    }
  };

  const toggleResultOpen = (accountId: string) => {
    setOpenResults((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  };

  if (loadingAccounts) {
    return (
      <LoadingBlock pad="80px 0" color="var(--color-text-secondary)" label={t('copy.loadingAccounts')} />
    );
  }

  if (loadError) {
    return (
      <div className="flex items-center justify-center py-20 text-[var(--color-danger,#ff5c5c)]">
        {loadError}
      </div>
    );
  }

  const stepBadge = (n: number) => (
    <span className="w-6 h-6 rounded-full bg-[var(--color-accent-primary)] text-white text-xs font-bold flex items-center justify-center">
      {n}
    </span>
  );

  const card = 'bg-[var(--color-bg-card)] border border-[var(--color-border-default)] rounded-xl p-5';

  // Reads as a control, not as incidental link text: bordered, filled, icon-led.
  const toggleAllBtn =
    'ml-auto inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border ' +
    'border-[var(--color-border-default)] bg-[var(--color-bg-hover)] text-xs font-semibold ' +
    'text-[var(--color-text-secondary)] whitespace-nowrap transition-colors ' +
    'hover:border-[var(--color-accent-primary)] hover:text-[var(--color-accent-primary)] ' +
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ' +
    'focus-visible:outline-[var(--color-accent-primary)]';

  return (
    <div className="flex flex-col gap-5 pb-28">
      {/* Step 1 — source */}
      <section className={card}>
        <div className="flex items-center gap-3 mb-1">
          {stepBadge(1)}
          <h2 className="text-base font-semibold text-[var(--color-text-primary)]">{t('copy.sourceTitle')}</h2>
        </div>
        <p className="text-sm text-[var(--color-text-secondary)] mb-4">{t('copy.sourceHint')}</p>
        <SearchBox value={sourceSearch} onChange={setSourceSearch} placeholder={t('copy.searchAccounts')} />
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 max-h-64 overflow-y-auto pr-1">
          {sourceCandidates.map((a) => (
            <button
              key={a.account_id}
              type="button"
              onClick={() => pickSource(a.account_id)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-colors ${
                sourceId === a.account_id
                  ? 'border-[var(--color-accent-primary)] bg-[var(--color-bg-active)]'
                  : 'border-[var(--color-border-default)] hover:bg-[var(--color-bg-hover)]'
              }`}
            >
              <AccountAvatar avatarUrl={a.avatar_url} platform={a.platform} size={32} />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-[var(--color-text-primary)] truncate">{accountLabel(a)}</span>
                {a.username && (
                  <span className="block text-xs text-[var(--color-text-tertiary)] truncate">@{a.username}</span>
                )}
              </span>
              {sourceId === a.account_id && (
                <FeatherIcon name="check" size={16} className="ml-auto text-[var(--color-accent-primary)]" />
              )}
            </button>
          ))}
          {sourceCandidates.length === 0 && (
            <p className="text-sm text-[var(--color-text-tertiary)] col-span-full py-4 text-center">{t('copy.noAccountsFound')}</p>
          )}
        </div>
      </section>

      {/* Step 2 — targets */}
      <section className={`${card} ${!sourceId ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="flex items-center gap-3 mb-1">
          {stepBadge(2)}
          <h2 className="text-base font-semibold text-[var(--color-text-primary)]">{t('copy.targetsTitle')}</h2>
          {targetIds.size > 0 && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[var(--color-bg-active)] text-[var(--color-accent-primary)] whitespace-nowrap">
              {t('copy.selectedCount', { count: targetIds.size })}
            </span>
          )}
          {(() => {
            const allOn =
              visibleTargetIds.length > 0 && visibleTargetIds.every((id) => targetIds.has(id));
            return (
              <button
                type="button"
                onClick={toggleAllVisibleTargets}
                aria-pressed={allOn}
                className={toggleAllBtn}
              >
                <FeatherIcon name={allOn ? 'square' : 'check-square'} size={13} />
                {allOn ? t('copy.clearAll') : t('copy.selectAll')}
              </button>
            );
          })()}
        </div>
        <p className="text-sm text-[var(--color-text-secondary)] mb-4">{t('copy.targetsHint')}</p>
        <SearchBox value={targetSearch} onChange={setTargetSearch} placeholder={t('copy.searchAccounts')} />
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 max-h-64 overflow-y-auto pr-1">
          {targetCandidates.map((a) => {
            const on = targetIds.has(a.account_id);
            return (
              <button
                key={a.account_id}
                type="button"
                onClick={() => toggleTarget(a.account_id)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-colors ${
                  on
                    ? 'border-[var(--color-accent-primary)] bg-[var(--color-bg-active)]'
                    : 'border-[var(--color-border-default)] hover:bg-[var(--color-bg-hover)]'
                }`}
              >
                <Checkbox checked={on} />
                <AccountAvatar avatarUrl={a.avatar_url} platform={a.platform} size={28} />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-[var(--color-text-primary)] truncate">{accountLabel(a)}</span>
                  {a.username && (
                    <span className="block text-xs text-[var(--color-text-tertiary)] truncate">@{a.username}</span>
                  )}
                </span>
              </button>
            );
          })}
          {targetCandidates.length === 0 && (
            <p className="text-sm text-[var(--color-text-tertiary)] col-span-full py-4 text-center">{t('copy.noAccountsFound')}</p>
          )}
        </div>
      </section>

      {/* Step 3 — what to copy */}
      <section className={`${card} ${!sourceId ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="flex items-center gap-3 mb-1">
          {stepBadge(3)}
          <h2 className="text-base font-semibold text-[var(--color-text-primary)]">{t('copy.itemsTitle')}</h2>
          {selectedItems.size > 0 && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[var(--color-bg-active)] text-[var(--color-accent-primary)] whitespace-nowrap">
              {t('copy.selectedCount', { count: selectedItems.size })}
            </span>
          )}
          <button
            type="button"
            onClick={toggleAllItems}
            aria-pressed={selectedItems.size === ALL_ITEM_KEYS.length}
            className={toggleAllBtn}
          >
            <FeatherIcon
              name={selectedItems.size === ALL_ITEM_KEYS.length ? 'square' : 'check-square'}
              size={13}
            />
            {selectedItems.size === ALL_ITEM_KEYS.length ? t('copy.clearAll') : t('copy.selectAll')}
          </button>
        </div>
        <p className="text-sm text-[var(--color-text-secondary)] mb-2">{t('copy.itemsHint')}</p>
        <p className="text-xs text-[var(--color-text-tertiary)] mb-4 flex items-center gap-1.5">
          <FeatherIcon name="info" size={13} />
          {t('copy.noContentNote')}
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {COPY_GROUPS.map((group) => {
            const allOn = group.items.every((i) => selectedItems.has(i.key));
            const someOn = group.items.some((i) => selectedItems.has(i.key));
            return (
              <div key={group.key} className="border border-[var(--color-border-default)] rounded-lg p-3">
                <button
                  type="button"
                  onClick={() => toggleGroup(group)}
                  className="w-full flex items-center gap-2.5 mb-2 group"
                >
                  <Checkbox checked={allOn} indeterminate={!allOn && someOn} />
                  <FeatherIcon name={group.icon} size={15} className="text-[var(--color-accent-primary)]" />
                  <span className="text-sm font-semibold text-[var(--color-text-primary)] group-hover:text-[var(--color-accent-primary)] transition-colors">
                    {t(`copy.groups.${group.key}`)}
                  </span>
                </button>
                <div className="flex flex-col gap-1">
                  {group.items.map((item) => (
                    <label
                      key={item.key}
                      className="flex items-start gap-2.5 px-2 py-1.5 rounded-md cursor-pointer hover:bg-[var(--color-bg-hover)] transition-colors"
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={selectedItems.has(item.key)}
                        onChange={() => toggleItem(item.key)}
                      />
                      <Checkbox checked={selectedItems.has(item.key)} className="mt-0.5" />
                      <span className="min-w-0">
                        <span className="block text-sm text-[var(--color-text-primary)]">
                          {t(`copy.items.${item.key}.label`)}
                        </span>
                        {item.note && (
                          <span className="block text-xs text-[var(--color-text-tertiary)]">
                            {t(`copy.items.${item.key}.note`)}
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Results */}
      {result && (
        <section className={card}>
          <div className="flex items-center gap-2 mb-4">
            <FeatherIcon name="check-circle" size={18} className="text-[var(--color-success,#3ddc84)]" />
            <h2 className="text-base font-semibold text-[var(--color-text-primary)]">{t('copy.resultsTitle')}</h2>
          </div>
          <div className="flex flex-col gap-2">
            {result.results.map((r) => {
              const counts = r.items.reduce(
                (acc, i) => ({ ...acc, [i.status]: (acc[i.status] || 0) + 1 }),
                {} as Record<string, number>,
              );
              const open = openResults.has(r.account_id);
              return (
                <div key={r.account_id} className="border border-[var(--color-border-default)] rounded-lg">
                  <button
                    type="button"
                    onClick={() => toggleResultOpen(r.account_id)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[var(--color-bg-hover)] rounded-lg transition-colors"
                  >
                    <FeatherIcon name={open ? 'chevron-down' : 'chevron-right'} size={16} className="text-[var(--color-text-tertiary)]" />
                    <span className="text-sm font-medium text-[var(--color-text-primary)] min-w-0 truncate text-left">
                      {nameById.get(r.account_id) || r.account_id}
                    </span>
                    <span className="ml-auto flex flex-wrap items-center justify-end gap-x-3 gap-y-0.5 text-xs">
                      {(['copied', 'skipped', 'forbidden', 'error'] as AgencyCopyItemStatus[]).map(
                        (s) =>
                          counts[s] ? (
                            <span key={s} className={`flex items-center gap-1 ${STATUS_META[s].className}`}>
                              <FeatherIcon name={STATUS_META[s].icon} size={13} />
                              {counts[s]} {t(`copy.status.${s}`)}
                            </span>
                          ) : null,
                      )}
                    </span>
                  </button>
                  {open && (
                    <div className="px-4 pb-3 flex flex-col gap-1.5">
                      {r.items.map((i) => (
                        <div key={i.key} className="flex items-start gap-2 text-sm">
                          <FeatherIcon
                            name={STATUS_META[i.status].icon}
                            size={14}
                            className={`mt-0.5 ${STATUS_META[i.status].className}`}
                          />
                          <span className="text-[var(--color-text-primary)]">{t(`copy.items.${i.key}.label`)}</span>
                          {i.detail && (
                            <span className="text-xs text-[var(--color-text-tertiary)] mt-0.5">— {i.detail}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {copyError && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg border border-[var(--color-danger,#ff5c5c)] text-[var(--color-danger,#ff5c5c)] text-sm">
          <FeatherIcon name="alert-triangle" size={16} />
          {copyError}
        </div>
      )}

      {/* Sticky action bar — left offset mirrors the main content margin so it never slips under the sidebar.
          Portaled so the main area's slide when the sidebar folds (a translate) can't anchor it. */}
      {createPortal(
        <div
          className="agency-copy-action-bar fixed bottom-0 right-0 z-40 pointer-events-none transition-all duration-200"
          style={{ left: sidebarWidth }}
        >
          <div className="w-full px-6 pb-4">
            <div className="pointer-events-auto flex items-center gap-4 bg-[var(--color-bg-card)] border border-[var(--color-border-default)] rounded-xl px-5 py-3 shadow-2xl">
              <span className="text-sm text-[var(--color-text-secondary)]">
                {sourceId
                  ? t('copy.summary', {
                      source: nameById.get(sourceId) || sourceId,
                      items: selectedItems.size,
                      targets: targetIds.size,
                    })
                  : t('copy.summaryEmpty')}
              </span>
              <button
                type="button"
                disabled={!canCopy}
                onClick={handleCopy}
                className={`ml-auto flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  canCopy
                    ? 'bg-[var(--color-accent-primary)] text-white hover:opacity-90'
                    : 'bg-[var(--color-bg-hover)] text-[var(--color-text-tertiary)] cursor-not-allowed'
                }`}
              >
                {copying ? <Spinner size={16} /> : <FeatherIcon name="copy" size={16} />}
                {copying ? t('copy.copying') : t('copy.copyButton')}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative">
      <FeatherIcon
        name="search"
        size={15}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-tertiary)]"
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-9 pr-3 py-2 rounded-lg bg-[var(--color-bg-hover)] border border-[var(--color-border-default)] text-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-tertiary)] outline-none focus:border-[var(--color-accent-primary)] transition-colors"
      />
    </div>
  );
}

function Checkbox({
  checked,
  indeterminate = false,
  className = '',
}: {
  checked: boolean;
  indeterminate?: boolean;
  className?: string;
}) {
  return (
    <span
      className={`w-4 h-4 shrink-0 rounded border flex items-center justify-center transition-colors ${
        checked || indeterminate
          ? 'bg-[var(--color-accent-primary)] border-[var(--color-accent-primary)]'
          : 'border-[var(--color-border-default)] bg-transparent'
      } ${className}`}
    >
      {checked && <FeatherIcon name="check" size={11} className="text-white" />}
      {!checked && indeterminate && <FeatherIcon name="minus" size={11} className="text-white" />}
    </span>
  );
}
